import React, { useState, useEffect, useRef } from 'react';
import i18next from 'i18next';
import clsx from 'clsx';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Paginator } from 'primereact/paginator';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';

import axiosInstance from '../../../config/axiosConfig';

import useSelectedCompanyStore from '../../../store/useSelectedCompanyStore';
import usePaymentsHistoryStore from '../../../store/usePaymentsHistoryStore';

const t = (i18nKey, params) => i18next.t(`userDashboard.billing.history.${i18nKey}`, params);

function History() {
    const [loading, setLoading] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(null);
    const [paymentDetailsLoading, setPaymentDetailsLoading] = useState(false);

    const [page, setPage] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);

    const toast = useRef(null);

    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);

    const paymentsHistory = usePaymentsHistoryStore((state) => state.paymentsHistory);
    const setPaymentsHistory = usePaymentsHistoryStore((state) => state.setPaymentsHistory);
    const clearPaymentsHistory = usePaymentsHistoryStore((state) => state.clearPaymentsHistory);

    useEffect(() => {
        fetchPayments();
    }, []);

    const fetchPayments = async (force = false) => {
        if (!force && paymentsHistory.length > 0) return;

        setLoading(true);

        try {
            const response = await axiosInstance.get(`/dashboard/finances/${selectedCompany?.company_id}/payments-history/list`, {
                params: {
                    page: page + 1,
                    limit: 10,
                },
            });
            setPaymentsHistory(response.data.items);
            setTotalRecords(response.data.count);
        } catch (error) {
            console.error('Error fetching payments:', error);
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Une erreur est survenue, veuillez réessayer plus tard.', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const fetchPaymentDetails = async (payment_id) => {
        if (paymentDetailsLoading) return;
        setPaymentDetailsLoading(true);

        try {
            const response = await axiosInstance.get(`/dashboard/finances/${selectedCompany?.company_id}/payments-history/${payment_id}`);
            setSelectedPayment(response.data);
            setDialogVisible(true);
        } catch (error) {
            console.error('Error fetching payment details:', error);
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec du chargement des détails du paiement', life: 3000 });
        } finally {
            setPaymentDetailsLoading(false);
            setLoadingDetails(null);
        }
    };

    const onPageChange = (event) => {
        setLoading(true);
        clearPaymentsHistory();
        setPage(event.page);
        fetchPayments(true);
    };

    const statusBodyTemplate = (rowData) => {
        let statusClass = '';
        switch (rowData.status) {
            case 'complete':
                statusClass = 'tag is-success';
                break;
            case 'paid':
                statusClass = 'tag is-success';
                break;
            case 'pending':
                statusClass = 'tag is-warning';
                break;
            case 'open':
                statusClass = 'tag is-warning';
                break;
            case 'incomplete':
                statusClass = 'tag is-danger';
                break;
            default:
                statusClass = 'tag is-light';
        }
        return <span className={statusClass}>{t(`${rowData.status}`)}</span>;
    };

    const paymentDetailsDialogFooter = (
        <div className="buttons">
            <Button label={t('close')} icon="pi pi-times" onClick={() => setDialogVisible(false)} />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <DataTable emptyMessage={t('no_billing_history')} value={loading ? Array(10).fill({}) : paymentsHistory} paginator={paymentsHistory.length > 10} rows={10} totalRecords={totalRecords} lazy first={page * 10} onPage={onPageChange} onRowClick={(e) => { fetchPaymentDetails(e.data.payment_id); setLoadingDetails(e.data.payment_id) }}>
                <Column field="payment_id" header={t('payment_id')} body={(rowData) => loading ? <Skeleton width="100%" height="2rem" /> : rowData.payment_id} />
                <Column field="amount" header={t('amount_ht')} body={(rowData) => loading ? <Skeleton width="100%" height="2rem" /> : `${rowData.amount} ${rowData.currency}`} />
                <Column field="status" header={t('status')} body={(rowData) => loading ? <Skeleton width="100%" height="2rem" /> : statusBodyTemplate(rowData)} />
                <Column header=""
                    body={(rowData) => loading ? <Skeleton width="100%" height="2rem" /> : (
                        <Button severity='info' label={t('details')} icon="pi pi-info-circle" loading={loadingDetails === rowData.payment_id} onClick={() => { fetchPaymentDetails(rowData.payment_id); setLoadingDetails(rowData.payment_id) }} outlined />
                    )}
                />
            </DataTable>

            <Dialog header={t('payment_details')} dismissableMask={true} visible={dialogVisible} style={{ width: '50vw' }} footer={paymentDetailsDialogFooter} onHide={() => setDialogVisible(false)}>
                {paymentDetailsLoading ? (
                    <div className="columns">
                        {[...Array(10)].map((_, i) => (
                            <div key={i} className="column is-12">
                                <Skeleton width="100%" height="25px" />
                            </div>
                        ))}
                    </div>
                ) : selectedPayment ? (
                    <table className="table is-fullwidth">
                        <tbody>
                            <tr>
                                <th>{t('payment_id')}:</th>
                                <td>{selectedPayment.payment_id || <span className="has-text-grey">{t('unset')}</span>}</td>
                            </tr>
                            {selectedPayment.credit_action ? (
                                <>
                                    <tr>
                                        <th>Note:</th>
                                        <td><span className="tag is-info">{t('admin_payment')}</span></td>
                                    </tr>
                                    <tr>
                                        <th>{t('credit_action')}:</th>
                                        <td>{selectedPayment.credit_action && t(`${selectedPayment.credit_action}`) || <span className="has-text-grey">{t('unset')}</span>}</td>
                                    </tr>
                                    <tr>
                                        <th>{t('credit_amount')}:</th>
                                        <td>{selectedPayment.credit_amount || <span className="has-text-grey">{t('unset')}</span>}</td>
                                    </tr>
                                    <tr>
                                        <th>{t('credits_before_action')}:</th>
                                        <td>{selectedPayment.credits_before_action || <span className="has-text-grey">{t('unset')}</span>}</td>
                                    </tr>
                                    <tr>
                                        <th>{t('credits_after_action')}:</th>
                                        <td>{selectedPayment.credits_after_action || <span className="has-text-grey">{t('unset')}</span>}</td>
                                    </tr>
                                </>
                            ) : (
                                <>
                                    <tr>
                                        <th>{t('amount_ht')}:</th>
                                        <td>{selectedPayment.amount} {selectedPayment.currency || <span className="has-text-grey">{t('unset')}</span>}</td>
                                    </tr>
                                    <tr>
                                        <th>{t('status')}:</th>
                                        <td>{selectedPayment.status || <span className="has-text-grey">{t('unset')}</span>}</td>
                                    </tr>
                                    <tr>
                                        <th>{t('payment_method')}:</th>
                                        <td>{selectedPayment.payment_method || <span className="has-text-grey">{t('unset')}</span>}</td>
                                    </tr>
                                    <tr>
                                        <th>{t('last_four_digits')}:</th>
                                        <td>{selectedPayment.card_last_four || <span className="has-text-grey">{t('unset')}</span>}</td>
                                    </tr>
                                    <tr>
                                        <th>{t('payment_type')}:</th>
                                        <td>{selectedPayment.payment_type || <span className="has-text-grey">{t('unset')}</span>}</td>
                                    </tr>
                                    <tr>
                                        <th>{t('description')}:</th>
                                        <td>{selectedPayment.description || <span className="has-text-grey">{t('unset')}</span>}</td>
                                    </tr>
                                    <tr>
                                        <th>{t('quantity')}:</th>
                                        <td>{selectedPayment.quantity || <span className="has-text-grey">{t('unset')}</span>}</td>
                                    </tr>
                                    <tr>
                                        <th>{t('recurrence')}:</th>
                                        <td>{selectedPayment.recurrency || <span className="has-text-grey">{t('unset')}</span>}</td>
                                    </tr>
                                </>
                            )}
                            <tr>
                                <th>{t('created_at')}:</th>
                                <td>{new Date(selectedPayment.created_at).toLocaleDateString('fr-FR') || <span className="has-text-grey">{t('unset')}</span>}</td>
                            </tr>
                            <tr>
                                <th>{t('updated_at')}:</th>
                                <td>{new Date(selectedPayment.updated_at).toLocaleDateString('fr-FR') || <span className="has-text-grey">{t('unset')}</span>}</td>
                            </tr>
                        </tbody>
                    </table>
                ) : (
                    <div className="has-text-centered">
                        <p className="has-text-grey">{t('no_data_available')}</p>
                    </div>
                )}
            </Dialog>
        </>
    );
}

export default History;